import React, { useState, useMemo } from "react"
import { FormikProps } from "formik"
import Select from "@ecom/ui/components/Select"
import * as styles from "./fields.module.scss"
import { FormValues } from "../../types"
import { useChoiseOfTimeSelectStyles } from "./styles"
import { DEFAULT_VALUE_SELECT_CHOISE_TIME } from "../helpers/variables"
import { pushToDataLayerOnce } from "../../../../helpers/pushToDataLayerOnce"

type Props = {
  name: string
  value?: string
  title?: string
  formik: FormikProps<FormValues>
  onBlur: (e: React.FocusEvent<any, Element>) => void
}

const WORK_START_HOUR = 8
const WORK_END_HOUR = 20
const WORK_END_MINUTE = 30
const MAX_TOTAL_MINUTES = WORK_END_HOUR * 60 + WORK_END_MINUTE // 1230

const DEFAULT_HOUR = "08"
const DEFAULT_MINUTE = "00"
const MINUTE_STEP = 15

const menuProps = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  style: { marginTop: 8 },
}

const clampTime = (hour: string, minute: string) => {
  const total = parseInt(hour, 10) * 60 + parseInt(minute, 10)
  if (total > MAX_TOTAL_MINUTES) return { hour: "20", minute: "30" }
  return { hour, minute }
}

const composeValue = (day: string, hour: string, minute: string) => {
  if (day === "Сегодня" || day === "Завтра") return `${day} ${hour}:${minute}`
  return day
}

const parseValue = (v?: string) => {
  if (!v) return { day: "", hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE }
  if (v === DEFAULT_VALUE_SELECT_CHOISE_TIME) {
    return { day: v, hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE }
  }

  const match = v.match(/^(Сегодня|Завтра)\s+(\d{2}):(\d{2})$/)
  if (match) {
    return { day: match[1], hour: match[2], minute: match[3] }
  }
  return { day: "", hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE }
}

const ColonSeparator = () => (
  <svg
    width="6"
    height="56"
    viewBox="0 0 6 56"
    fill="none"
    xmlns="http://w3.org"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M0 38.0391C0 37.3047 0.226562 36.6875 0.679688 36.1875C1.14844 35.6719 1.82031 35.4141 2.69531 35.4141C3.57031 35.4141 4.23438 35.6719 4.6875 36.1875C5.15625 36.6875 5.39062 37.3047 5.39062 38.0391C5.39062 38.7578 5.15625 39.3672 4.6875 39.8672C4.23438 40.3672 3.57031 40.6172 2.69531 40.6172C1.82031 40.6172 1.14844 40.3672 0.679688 39.8672C0.226562 39.3672 0 38.7578 0 38.0391ZM0.0234375 17.3438C0.0234375 16.6094 0.25 15.9922 0.703125 15.4922C1.17188 14.9766 1.84375 14.7188 2.71875 14.7188C3.59375 14.7188 4.25781 14.9766 4.71094 15.4922C5.17969 15.9922 5.41406 16.6094 5.41406 17.3438C5.41406 18.0625 5.17969 18.6719 4.71094 19.1719C4.25781 19.6719 3.59375 19.9219 2.71875 19.9219C1.84375 19.9219 1.17188 19.6719 0.703125 19.1719C0.25 18.6719 0.0234375 18.0625 0.0234375 17.3438Z"
      fill="#757575"
    />
  </svg>
)

export const ChoiceOfTime = ({ name, value, formik, title, onBlur }: Props) => {
  const selectStyles = useChoiseOfTimeSelectStyles()

  const initial = parseValue(value)
  const [day, setDay] = useState(initial.day)
  const [hour, setHour] = useState(initial.hour)
  const [minute, setMinute] = useState(initial.minute)

  const showTimeSelects = day === "Сегодня" || day === "Завтра"

  const timeItems = useMemo(() => {
    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()

    const isTodayOutTime =
      currentHour > WORK_END_HOUR ||
      (currentHour === WORK_END_HOUR && currentMinute >= WORK_END_MINUTE)

    return [
      { value: DEFAULT_VALUE_SELECT_CHOISE_TIME, label: DEFAULT_VALUE_SELECT_CHOISE_TIME },
      {
        value: "Сегодня",
        label: "Сегодня",
        disabled: isTodayOutTime,
      },
      { value: "Завтра", label: "Завтра" },
    ]
  }, [])

  const hourItems = useMemo(() => {
    const now = new Date()
    const currentHour = now.getHours()

    return Array.from({ length: WORK_END_HOUR - WORK_START_HOUR + 1 }, (_, i) => {
      const hNum = WORK_START_HOUR + i
      const hStr = String(hNum).padStart(2, "0")

      const isDisabled = day === "Сегодня" && hNum < currentHour

      return { value: hStr, label: hStr, disabled: isDisabled }
    })
  }, [day])

  const minuteItems = useMemo(() => {
    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()
    const selectedHourNum = parseInt(hour, 10)

    return Array.from({ length: 60 / MINUTE_STEP }, (_, i) => {
      const mNum = i * MINUTE_STEP
      const mStr = String(mNum).padStart(2, "0")

      let isDisabled = false

      if (selectedHourNum === 20 && mNum > 30) {
        isDisabled = true
      }

      if (day === "Сегодня" && selectedHourNum === currentHour && mNum < currentMinute) {
        isDisabled = true
      }

      return { value: mStr, label: mStr, disabled: isDisabled }
    })
  }, [day, hour])

  const commit = (d: string, h: string, m: string) => {
    pushToDataLayerOnce("choosingCallTime", {
      event: "choosingCallTime",
      result: "success",
      eventAction: composeValue(d, h, m),
      eventLabel: globalThis?.location?.href,
      placement: "main",
    })

    formik.setFieldValue(name, composeValue(d, h, m))
  }

  const handleDayChange = (e: { target: { value: string } }) => {
    const nextDay = e.target.value

    const nowCheck = new Date()
    const isOut =
      nowCheck.getHours() > 20 || (nowCheck.getHours() === 20 && nowCheck.getMinutes() >= 30)
    const finalDay = nextDay === "Сегодня" && isOut ? "Завтра" : nextDay

    setDay(finalDay)

    let nextHour = hour
    let nextMinute = minute

    const now = new Date()
    if (finalDay === "Сегодня") {
      const currentHour = now.getHours()
      if (parseInt(nextHour, 10) < currentHour) {
        nextHour = String(Math.max(WORK_START_HOUR, currentHour)).padStart(2, "0")
      }
      const currentMinute = now.getMinutes()
      if (parseInt(nextHour, 10) === currentHour && parseInt(nextMinute, 10) < currentMinute) {
        const nextValidMinute = Math.ceil(currentMinute / MINUTE_STEP) * MINUTE_STEP
        nextMinute = nextValidMinute >= 60 ? "00" : String(nextValidMinute).padStart(2, "0")
        if (nextValidMinute >= 60) {
          nextHour = String(parseInt(nextHour, 10) + 1).padStart(2, "0")
        }
      }
    } else if (finalDay === "Завтра") {
      nextHour = DEFAULT_HOUR
      nextMinute = DEFAULT_MINUTE
    }

    const clamped = clampTime(nextHour, nextMinute)
    setHour(clamped.hour)
    setMinute(clamped.minute)
    commit(finalDay, clamped.hour, clamped.minute)
  }

  const handleHourChange = (e: { target: { value: string } }) => {
    const nextHour = e.target.value
    let nextMinute = minute

    const now = new Date()
    if (day === "Сегодня" && parseInt(nextHour, 10) === now.getHours()) {
      if (parseInt(nextMinute, 10) < now.getMinutes()) {
        const nextValidMinute = Math.ceil(now.getMinutes() / MINUTE_STEP) * MINUTE_STEP
        nextMinute = nextValidMinute >= 60 ? "00" : String(nextValidMinute).padStart(2, "0")
      }
    }

    const clamped = clampTime(nextHour, nextMinute)
    setHour(clamped.hour)
    setMinute(clamped.minute)
    commit(day, clamped.hour, clamped.minute)
  }

  const handleMinuteChange = (e: { target: { value: string } }) => {
    const clamped = clampTime(hour, e.target.value)
    setHour(clamped.hour)
    setMinute(clamped.minute)
    commit(day, clamped.hour, clamped.minute)
  }

  return (
    <div className={styles.selectField}>
      {title && <h4 className={styles.choiceProductTitle}>{title}</h4>}
      <div className={styles.selectsContainer}>
        <Select
          fullWidth
          inputsColor="white"
          items={timeItems}
          value={day}
          onChange={handleDayChange}
          onBlur={onBlur}
          name={name}
          MenuProps={menuProps}
          minHeight={56}
        />

        {showTimeSelects && (
          <div className={styles.timeRow ?? ""} style={{ display: "flex", gap: 12 }}>
            <Select
              inputsColor="white"
              items={hourItems}
              value={hour}
              onChange={handleHourChange}
              onBlur={onBlur}
              name={`${name}-hour`}
              MenuProps={menuProps}
              classes={selectStyles}
              minHeight={56}
            />

            <ColonSeparator />

            <Select
              inputsColor="white"
              items={minuteItems}
              value={minute}
              onChange={handleMinuteChange}
              onBlur={onBlur}
              name={`${name}-minute`}
              MenuProps={menuProps}
              classes={selectStyles}
              minHeight={56}
            />
          </div>
        )}
      </div>
    </div>
  )
}
